<template>
  <form class="login-form" @submit.prevent="submit">
    <NotificationBanner v-if="formError" is-danger :message="formError" />

    <FormTextInput
      v-model="formData.email"
      name="email"
      type="email"
      :label="$t('field-labels.email')"
      :is-disabled="isLoading"
      autocomplete="on"
      is-required
    />

    <FormTextInput
      v-model="formData.password"
      name="password"
      type="password"
      :label="$t('field-labels.password')"
      :is-disabled="isLoading"
      autocomplete="on"
      is-required
    />

    <div>
      {{ $t('general.forgotten-password') }}<br />
      <RouterLink :to="{ path: '/password-reset' }">
        {{ $t('general.password-reset') }}
      </RouterLink>
    </div>

    <FormButtonGroup>
      <FormButton :is-busy="isLoading">
        {{ $t('general.login') }}
      </FormButton>

      <RouterLinkButton v-show="!isLoading" :to="{ path: '/register' }">
        {{ $t('bowler-registration.heading') }}
      </RouterLinkButton>
    </FormButtonGroup>
  </form>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import FormButton from '@/components/common/FormButton.vue'
import FormButtonGroup from '@/components/common/FormButtonGroup.vue'
import FormTextInput from '@/components/common/FormTextInput.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import RouterLinkButton from '@/components/common/RouterLinkButton.vue'
import { useAuthStore } from '@/store/bowler-app/auth'
import { AuthPayload } from '@/store/bowler-app/auth.types'
import { getErrorMessage } from '@/utils/error-handling'

const router = useRouter()

/* local state */

const formData = ref<AuthPayload>({ email: '', password: '' })
const formError = ref<string | null>()
const isLoading = ref<boolean>(false)

/* actions */

const submit = async (): Promise<void> => {
  isLoading.value = true
  formError.value = null

  try {
    await useAuthStore().login(formData.value)
    await router.push({ path: '/' })
  } catch (e) {
    formError.value = getErrorMessage(e as Error)
    console.group('Login failed')
    console.error(formError.value)
    console.groupEnd()
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/globals';

.login-form {
  display: flex;
  flex-flow: column nowrap;
  gap: 1em;
}
</style>
